import { createAction, type QueuedAction } from "@/v2/hooks/useActionQueue";
import { formatEntryId } from "./entryId";
import {
	type ActionDeps,
	ApiError,
	actingHeaders,
	apiCall,
	combineRollback,
	plural,
	refreshBoth,
} from "./types";

/**
 * Bulk delete via the v2 atomic endpoint.
 *
 *   POST /v2/entries/delete { ids: ["ent_<n>", …] }
 *
 * Server-side this runs every soft-delete inside one Prisma `$transaction`,
 * so any failure (404 / 403 ENTRY_EXPORTED) rolls back the whole batch. The
 * legacy `/records/delete` endpoint stays available for v1 callers, but the
 * v2 layer routes through here so /entries shares the same path the cheap-
 * path PATCH `status="deleted"` already uses for single rows.
 *
 * Optimistic update strips every id from both the legacy entry store and the
 * v2 cache; on failure the rollback restores both. The post-action refresh
 * reconciles to the server's actual state.
 */
export function deleteAction(
	ids: number[],
	deps: ActionDeps,
	timekeeperId?: string | null,
): QueuedAction {
	const idSet = new Set(ids);
	const wireIds = ids.map(formatEntryId);
	const v2IdSet = new Set(wireIds);
	const rollbackLegacy = deps.updateEntries((prev) =>
		prev.filter((e) => !idSet.has(e.id)),
	);
	const rollbackV2 =
		deps.updateEntriesV2?.((prev) => prev.filter((e) => !v2IdSet.has(e.id))) ??
		null;
	const rollback = combineRollback(rollbackLegacy, rollbackV2);
	const n = ids.length;

	return createAction({
		execute: () =>
			apiCall(deps, {
				endpoint: "/v2/entries/delete",
				method: "POST",
				body: { ids: wireIds },
				headers: actingHeaders(timekeeperId),
			}),
		rollback,
		toast: {
			loading: `Deleting ${n} ${plural(n)}…`,
			success: `Deleted ${n} ${plural(n)}`,
			error: (err) => {
				if (err instanceof ApiError) {
					if (err.code === "ENTRY_EXPORTED")
						return "Some entries are exported and can't be deleted";
					if (err.code === "ENTRY_NOT_FOUND")
						return "One or more entries no longer exist";
				}
				return "Failed to delete entries";
			},
		},
		onSuccess: () => refreshBoth(deps),
		onError: () => refreshBoth(deps),
	});
}
